/* eslint-disable @typescript-eslint/no-explicit-any */
// @flow
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Grid, Icon, Segment, Label, Popup, SemanticICONS, SemanticCOLORS } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { DateTime } from "luxon";
import Responsive from "@/cms/components/Common/Responsive";

import { BonusStatuses, TransactionStatuses, TypesOfReward } from "../misc/Types";
import { requestPayment as requestPaymentCall, requestReferralPaymentCall } from "../misc/Api";

import { getSpecificTopDealUrl } from "../../TopDeals/misc/Routes";

import "./Reward.scss";
import type { BonusT } from "../misc/Types";

type BonusProps = {
  content: BonusT;
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateContent: Function | undefined;
  type?: string;
};

const Reward = ({ content, updateContent, type }: BonusProps) => {
  const { t } = useTranslation();
  const [buttonLoading, setButtonLoading] = useState(false);

  const isReferralReward = !!content.referralRelationId;

  const renderAmountLabel = (amount?: number) => {
    const labelString = amount && amount > 0 ? amount + "€" : <Icon name="gift" style={{ margin: "auto" }} />;
    return (
      <Label color="green" size="large">
        {labelString}
      </Label>
    );
  };

  const requestPayment = async (transactionId: string) => {
    setButtonLoading(true);
    try {
      const updatedTransaction = await requestPaymentCall(transactionId);
      updateContent?.(updatedTransaction);
      setButtonLoading(false);
    } catch (err) {
      console.error(err);
      setButtonLoading(false);
    }
  };
  const requestReferralPayment = async (transactionId: string) => {
    setButtonLoading(true);
    try {
      const updatedTransaction = await requestReferralPaymentCall(transactionId);
      updateContent?.(updatedTransaction);
      setButtonLoading(false);
    } catch (err) {
      console.error(err);
      setButtonLoading(false);
    }
  };

  const renderRequestButton = (transactionId: string) => (
    <Button
      basic
      primary
      fluid
      disabled={buttonLoading}
      loading={buttonLoading}
      onClick={() => {
        isReferralReward ? requestReferralPayment(transactionId) : requestPayment(transactionId);
      }}
    >
      {t("rewards.misc.requestPayment")}
    </Button>
  );

  const renderIconWithPopup = (iconName: SemanticICONS, iconColor: SemanticCOLORS = "black", tooltip: string) => (
    <Popup
      trigger={<Icon name={iconName} color={iconColor} data-testid="bonus-icon" />}
      content={t(tooltip)}
      basic
      inverted
      on={["hover", "click", "focus"]}
    />
  );

  let special: React.JSX.Element;
  const renderStatusIcon = (transaction: any, bonus?: any, transactionId?: string) => {
    let statusString = "";
    let icon;

    switch (transaction) {
      case TransactionStatuses.OPEN:
        statusString = `rewards.status.${transaction}`;
        icon = renderIconWithPopup("circle", "yellow", statusString);
        break;
      case TransactionStatuses.REJECTED:
        statusString = `rewards.status.${transaction}`;
        icon = renderIconWithPopup("cancel", "red", statusString);
        break;
      case TransactionStatuses.CONFIRMED:
        if (!bonus) {
          statusString = "rewards.status.UNKNOWN";
          icon = <Icon name="question circle" />;
          break;
        }
        switch (bonus) {
          case BonusStatuses.PAYMENT_PAID:
            statusString = `rewards.status.${bonus}`;
            icon = renderIconWithPopup("check", "green", statusString);
            break;
          case BonusStatuses.PAYMENT_REQUESTED:
            statusString = `rewards.status.${bonus}`;
            icon = renderIconWithPopup("circle", "green", statusString);
            break;
          case BonusStatuses.WAITING_FOR_PAYMENT_REQUEST:
            icon = renderIconWithPopup("circle", "yellow", `rewards.status.${bonus}`);
            special = renderRequestButton(transactionId ?? "");
            statusString = "";
            break;
          default:
            statusString = "rewards.status.UNKNOWN";
            icon = renderIconWithPopup("question", "black", statusString);
            break;
        }
        break;
      default:
        statusString = "rewards.status.UNKNOWN";
        icon = renderIconWithPopup("question", "black", statusString);
        break;
    }

    return (
      <>
        <Responsive {...Responsive.onlyMobile}>
          <div className="bonus-status-field">{icon}</div>
        </Responsive>
        <Responsive minWidth={Responsive.onlyTablet.minWidth}>
          <div className="bonus-status-field">
            {t(statusString)}
            {special || icon}
          </div>
        </Responsive>
      </>
    );
  };

  const segmentClassName = (type?: string): string => {
    const base = "bonus-segment";
    if (!type) {
      return base;
    }
    return classNames(base, "reward-status-" + type);
  };

  const renderMainContent = (content: BonusT) => {
    const link =
      content.topDealId && content.topDealTitle ? getSpecificTopDealUrl(content.topDealId, content.topDealTitle) : null;
    return (
      <Segment className={segmentClassName(type)}>
        <div className="bonus-inner-div">
          <Grid divided>
            <Grid.Column computer={2} tablet={2} mobile={4} verticalAlign="middle">
              {renderAmountLabel(content.offerBonusPrice)}
            </Grid.Column>
            <Grid.Column computer={4} tablet={4} only="tablet computer" verticalAlign="middle">
              <strong>{t("rewards.type." + (content.offerBonusType ? content.offerBonusType : "UNKNOWN"))}</strong>
              {content.offerBonusType === TypesOfReward.PROMOTIONAL_AWARDS ? (
                <div className="bonus-payout-date">{content.offerBonusTitle}</div>
              ) : (
                <>
                  {content.couponCode && (
                    <Responsive minWidth={Responsive.onlyTablet.minWidth}>
                      <div className="bonus-desktop-code">{content.couponCode}</div>
                    </Responsive>
                  )}
                  {content.topDealBonusEarliestPayDate && !content.couponCode && (
                    <div className="bonus-payout-date">
                      {"Auszahlung am: " +
                        DateTime.fromISO(content.topDealBonusEarliestPayDate).toFormat("rewards.dd.MM.yyyy")}
                    </div>
                  )}
                </>
              )}
            </Grid.Column>

            <Grid.Column computer={3} tablet={3} mobile={9} verticalAlign="middle">
              {content.offerTariffName || content.offerCompanyName}
            </Grid.Column>

            <Grid.Column computer={3} tablet={3} only="tablet computer" verticalAlign="middle">
              {link && <Link to={link}>{t("rewards.misc.viewDetails")}</Link>}
            </Grid.Column>

            <Grid.Column computer={4} tablet={4} mobile={3} verticalAlign="middle">
              {renderStatusIcon(content.transactionStatus, content.bonusStatus, content.transactionId)}
            </Grid.Column>
          </Grid>
        </div>
      </Segment>
    );
  };

  return (
    <>
      <Responsive {...Responsive.onlyMobile}>
        <Segment.Group className="bonus-segment-group">
          {renderMainContent(content)}
          {content.offerBonusType === TypesOfReward.PROMOTIONAL_AWARDS && (
            <Segment secondary>
              <div className="bonus-mobile-code">
                <strong>{t("rewards.type." + (content.offerBonusType || "UNKNOWN")) + " : "}</strong>
                <span>{content.offerBonusTitle}</span>
              </div>
            </Segment>
          )}
          {content.couponCode && (
            <Segment secondary>
              <div className="bonus-mobile-code">
                <strong>{t("rewards.type." + (content.offerBonusType || "UNKNOWN")) + " : "}</strong>
                <span>{content.couponCode}</span>
              </div>
            </Segment>
          )}
          {content.transactionStatus === TransactionStatuses.CONFIRMED &&
            content.bonusStatus === BonusStatuses.WAITING_FOR_PAYMENT_REQUEST && (
              <Segment secondary>{renderRequestButton(content.transactionId)}</Segment>
            )}
        </Segment.Group>
      </Responsive>
      <Responsive minWidth={Responsive.onlyTablet.minWidth}>{renderMainContent(content)}</Responsive>
    </>
  );
};

export default Reward;
